import { inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { Result } from '@core/models/common.model';
import { mapHttpErrorToResult } from '@shared/utils/api-response.utils';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly baseUrl = environment.BaseUrl;
  private readonly http = inject(HttpClient);
  private readonly defaultHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    Accept: 'application/json',
  });

  private buildUrl(endpoint: string): string {
    return `${this.baseUrl}/${endpoint.replace(/^\/+/, '')}`;
  }

  private handleError(endpoint: string) {
    return (err: unknown) => {
      console.error(`API ${endpoint} failed`, err);
      return throwError(() => err);
    };
  }

  get<T>(
    endpoint: string,
    options?: {
      headers?: HttpHeaders;
      params?: HttpParams | Record<string, string | number | boolean | null | undefined>;
    },
  ): Observable<T> {
    const params =
      options?.params instanceof HttpParams
        ? options.params
        : new HttpParams({ fromObject: (options?.params as Record<string, string>) || {} });

    return this.http
      .get<T>(this.buildUrl(endpoint), {
        headers: options?.headers || this.defaultHeaders,
        params,
      })
      .pipe(catchError(this.handleError(endpoint)));
  }

  post<T, B = unknown>(
    endpoint: string,
    body: B,
    options?: { headers?: HttpHeaders; params?: HttpParams },
  ): Observable<T> {
    return this.http
      .post<T>(this.buildUrl(endpoint), body, {
        headers: options?.headers ?? this.defaultHeaders,
        params: options?.params,
      })
      .pipe(catchError(this.handleError(endpoint)));
  }

  put<T, B = unknown>(
    endpoint: string,
    body: B,
    options?: { headers?: HttpHeaders; params?: HttpParams },
  ): Observable<T> {
    return this.http
      .put<T>(this.buildUrl(endpoint), body, {
        headers: options?.headers ?? this.defaultHeaders,
        params: options?.params,
      })
      .pipe(catchError(this.handleError(endpoint)));
  }

  patch<T, B = unknown>(
    endpoint: string,
    body: B,
    options?: { headers?: HttpHeaders; params?: HttpParams },
  ): Observable<T> {
    return this.http
      .patch<T>(this.buildUrl(endpoint), body, {
        headers: options?.headers ?? this.defaultHeaders,
        params: options?.params,
      })
      .pipe(catchError(this.handleError(endpoint)));
  }

  delete<T>(endpoint: string, options?: { headers?: HttpHeaders; params?: HttpParams }): Observable<T> {
    return this.http
      .delete<T>(this.buildUrl(endpoint), {
        headers: options?.headers ?? this.defaultHeaders,
        params: options?.params,
      })
      .pipe(catchError(this.handleError(endpoint)));
  }

  handleApiError<T>(error: unknown): Result<T> {
    return mapHttpErrorToResult<T>(error);
  }
}
